import React, { useEffect } from 'react';
import { useLocation, useNavigate, useParams, Navigate, Routes, Route, BrowserRouter } from 'react-router-dom';
import { useDispatch } from 'react-redux';
import { LoginPage } from './auth/components/login.view';
import { Main } from './main/components/main.container';
import { refreshSession } from './account/account.util';
import { LOCATION_CHANGE, setNavigate } from './core/router/react-router-redux-compat';
import { Loading } from './common/components/loading.component';

function NavigationBinder() {
  const navigate = useNavigate();
  const location = useLocation();
  const dispatch = useDispatch();

  useEffect(() => {
    setNavigate(navigate);
  }, [navigate]);

  useEffect(() => {
    dispatch({
      type: LOCATION_CHANGE,
      payload: { location },
    });
  }, [location, dispatch]);

  return null;
}

function AuthGate({ children }: { children: React.ReactNode }) {
  const [ready, setReady] = React.useState(false);
  const [authed, setAuthed] = React.useState(false);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const tokens = await refreshSession();
        if (!cancelled) {
          setAuthed(!!tokens);
        }
      } catch {
        if (!cancelled) setAuthed(false);
      } finally {
        if (!cancelled) setReady(true);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  if (!ready) return <Loading />;
  if (!authed) return <Navigate to="/login" replace />;
  return <>{children}</>;
}

export function withRouter<P extends object>(Component: React.ComponentType<P & RouterInjectedProps>) {
  function WithRouter(props: P) {
    const navigate = useNavigate();
    const location = useLocation();
    const params = useParams();
    const history = {
      push: (path: string | { pathname: string }) => {
        if (typeof path === 'string') navigate(path);
        else navigate(path.pathname);
      },
      replace: (path: string) => navigate(path, { replace: true }),
      goBack: () => navigate(-1),
    };
    const match = { params };
    return <Component {...props} history={history} location={location} match={match} navigate={navigate} />;
  }
  return WithRouter;
}

export type RouterInjectedProps = {
  history: {
    push: (path: string | { pathname: string }) => void;
    replace: (path: string) => void;
    goBack: () => void;
  };
  location: ReturnType<typeof useLocation>;
  match: { params: Record<string, string | undefined> };
  navigate: ReturnType<typeof useNavigate>;
};

export function AppRouter() {
  return (
    <BrowserRouter>
      <NavigationBinder />
      <Routes>
        <Route path="/login" element={<LoginPage />} />
        <Route
          path="/admin/*"
          element={
            <AuthGate>
              <Main />
            </AuthGate>
          }
        />
        <Route path="/" element={<Navigate to="/admin/dashboard" replace />} />
        <Route path="*" element={<Navigate to="/admin/dashboard" replace />} />
      </Routes>
    </BrowserRouter>
  );
}
